import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Typography,
  Table,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip,
  Checkbox,
  InputLabel,
  Box,
  Backdrop,
  CircularProgress,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import { verificarEtapaAnterior } from "../utils/etapaHelpers";

import useUserRoles from "hooks/useUserRoles";
import useAudienciaEditorImages from "./hooks/useAudienciaEditorImages";

import ProcesosApi from "api/procesosApi";

import ReactQuill from "react-quill";
import styled from "styled-components";
import "react-quill/dist/quill.snow.css";

const StyledEditor = styled(ReactQuill)`
  .ql-toolbar {
    background-color: #f5f5f5;
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    min-height: 200px;
    font-size: 16px;
    border-radius: 0 0 8px 8px;
    font-family: "Roboto", sans-serif;
  }
  .ql-editor {
    padding: 16px;
    line-height: 1.5;
  }
  .ql-toolbar button:hover {
    background-color: #e0e0e0;
  }
  .ql-editor img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 10px auto;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ContainerResultado = ({ proceso, audiencia: initialAudiencia, onAudienciaChange }) => {
  const { user, roles } = useUserRoles();
  const [consideraciones, setConsideraciones] = useState("");
  const [propuestaPago, setPropuestaPago] = useState("");
  const [resolucionPago, setResolucionPago] = useState("");
  const quillConsideracionesRef = useRef(null);
  const quillPropuestaPagoRef = useRef(null);
  const quillResolucionPagoRef = useRef(null);
  const [checkedCumplida, setCheckedCumplida] = useState(false);
  const [acreedoresDeudas, setAcreedoresDeudas] = useState({});
  const [resultado, setResultado] = useState("");
  const [porcPostivo, setPorcPositivo] = useState(0);
  const [porcNegativo, setPorcNegativo] = useState(0);
  const [porcAusente, setPorcAusente] = useState(0);
  const [porcSinVoto, setPorcSinVoto] = useState(0);
  const [negociaciones, setNegociaciones] = useState([]);
  const [audiencia, setAudiencia] = useState(initialAudiencia);
  const [ordenActual] = useState(6);
  const [verificacion, setVerificacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Hook personalizado para manejo de imágenes específico de audiencias
  const {
    uploadingImage,
    imageError,
    setupImageHandler,
    processImagesForPDF,
    getQuillModules,
    getQuillFormats,
    validateContent,
    cleanOrphanImages,
    clearError,
    resetImageCounters,
  } = useAudienciaEditorImages(proceso?.identificador, initialAudiencia?.id, "resultado");

  const fetchDatosIniciales = async () => {
    try {
      setLoading(true);
      const etapaNegociacion = audiencia.etapas.find((etapa) => etapa.orden === 5);
      const datosNegociaciones = etapaNegociacion.resultado?.negociaciones || [];
      setNegociaciones(datosNegociaciones);
      setResultado(etapaNegociacion.resultado?.resultado);
      setPorcPositivo(etapaNegociacion.resultado?.porcPositivo);
      setPorcNegativo(etapaNegociacion.resultado?.porcNegativo);
      setPorcAusente(etapaNegociacion.resultado?.porcAusente);
      setPorcSinVoto(etapaNegociacion.resultado?.porcSinVoto);
    } catch (error) {
      console.error("Error obteniendo los datos iniciales:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAudiencia(initialAudiencia);
    fetchDatosIniciales();
    if (initialAudiencia && initialAudiencia.etapas) {
      setVerificacion(verificarEtapaAnterior(initialAudiencia.etapas, ordenActual));
    }
    // Resetear contadores de imágenes cuando cambie la audiencia
    resetImageCounters();
  }, [user, proceso, initialAudiencia, resetImageCounters]);

  // Configurar manejador de imagen cuando el ref esté listo
  useEffect(() => {
    const timer = setTimeout(() => {
      setupImageHandler(quillConsideracionesRef, "consideraciones", (imageInfo) => {
        console.log("Imagen subida en consideraciones:", imageInfo);
      });
      setupImageHandler(quillPropuestaPagoRef, "propuestaPago", (imageInfo) => {
        console.log("Imagen subida en propuesta de pago:", imageInfo);
      });
      setupImageHandler(quillResolucionPagoRef, "resolucionPago", (imageInfo) => {
        console.log("Imagen subida en propuesta de pago:", imageInfo);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [consideraciones, propuestaPago, setupImageHandler]);

  useEffect(() => {
    const inicializarAcreedores = () => {
      const etapaResultado = initialAudiencia?.etapas?.find((etapa) => etapa.orden === ordenActual);
      if (etapaResultado?.resultado?.consideraciones) {
        setConsideraciones(etapaResultado.resultado.consideraciones);
      }
      if (etapaResultado?.resultado?.propuesta_pago) {
        setPropuestaPago(etapaResultado.resultado.propuesta_pago);
      }
      if (etapaResultado?.resultado?.resolucion) {
        setResolucionPago(etapaResultado.resultado.resolucion);
      }
      if (etapaResultado.estado === "CUMPLIDA") setCheckedCumplida(true);
      if (etapaResultado.resultado?.info_acreedores) {
        setAcreedoresDeudas(etapaResultado.resultado?.info_acreedores);
      } else {
        const acreedoresAgrupados = negociaciones.reduce((acc, negociacion) => {
          const acreedor = negociacion.nombre_acreedor || "Sin Acreedor";
          if (!acc[acreedor]) {
            acc[acreedor] = {
              instruccion_pago: "",
              acuerdo_bilateral: false,
            };
          }
          return acc;
        }, {});
        setAcreedoresDeudas(acreedoresAgrupados);
      }
    };
    inicializarAcreedores();
  }, [negociaciones]);

  const handleSave = async () => {
    // Limpiar errores de imagen
    clearError();
    if (resultado?.includes("ACUERDO")) {
      const consideracionesValidation = validateContent(consideraciones);
      if (!consideracionesValidation.isValid) {
        alert("Error en las Consideraciones: " + consideracionesValidation.errors.join(", "));
        return;
      }
    }
    const acreedoresConAcuerdoBilateral = Object.entries(acreedoresDeudas).filter(
      ([, datos]) => datos.acuerdo_bilateral === true
    );
    if (acreedoresConAcuerdoBilateral.length > 0) {
      const propuestaPagoValidation = validateContent(propuestaPago);
      if (!propuestaPagoValidation.isValid) {
        alert("Error en la Propuesta de Pago: " + propuestaPagoValidation.errors.join(", "));
        return;
      }
      const resolucionPagoValidation = validateContent(resolucionPago);
      if (!resolucionPagoValidation.isValid) {
        alert("Error en la Resolución de Pago: " + resolucionPagoValidation.errors.join(", "));
        return;
      }
    }
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas guardar la Etapa de Resultado?`
    );
    if (!confirmAction) return;
    try {
      setSaving(true);
      // Procesar imágenes para PDF
      const parser = new DOMParser();
      const htmlConsideracionesProcesadas = await processImagesForPDF(consideraciones);
      const docConsideraciones = parser.parseFromString(htmlConsideracionesProcesadas, "text/html");
      const htmlLimpioConsideraciones = docConsideraciones.body.innerHTML;
      let htmlLimpioPropuestaPago = null;
      let htmlLimpioResolucionPago = null;
      if (acreedoresConAcuerdoBilateral.length > 0) {
        const htmlPropuestaPagoProcesadas = await processImagesForPDF(propuestaPago);
        const docPropuestaPago = parser.parseFromString(htmlPropuestaPagoProcesadas, "text/html");
        htmlLimpioPropuestaPago = docPropuestaPago.body.innerHTML;
        const htmlResolucionPagoProcesadas = await processImagesForPDF(resolucionPago);
        const docResolucionPago = parser.parseFromString(htmlResolucionPagoProcesadas, "text/html");
        htmlLimpioResolucionPago = docResolucionPago.body.innerHTML;
      }
      const updatedAudiencia = {
        ...audiencia,
        estado: "REALIZADA",
        etapas: audiencia.etapas.map((etapa) =>
          etapa.orden === ordenActual // RESULTADO
            ? {
                ...etapa,
                estado: checkedCumplida ? "CUMPLIDA" : "PENDIENTE",
                resultado: {
                  info_acreedores: acreedoresDeudas,
                  consideraciones: htmlLimpioConsideraciones,
                  propuesta_pago: htmlLimpioPropuestaPago,
                  resolucion: htmlLimpioResolucionPago,
                },
              }
            : etapa
        ),
      };
      await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);
      // Limpiar imágenes huérfanas después de guardar
      console.log("Iniciando limpieza de imágenes huérfanas después de guardar...");
      try {
        const cleanResult = await cleanOrphanImages({
          consideraciones: consideraciones,
        });
        if (cleanResult.success) {
          console.log(`Limpieza completada: ${cleanResult.deleted_count || 0} archivos eliminados`);
          if (cleanResult.deleted_files && cleanResult.deleted_files.length > 0) {
            console.log("Archivos eliminados:", cleanResult.deleted_files);
          }
        } else {
          console.warn("Error en limpieza de imágenes:", cleanResult.error);
        }
      } catch (cleanError) {
        console.error("Error ejecutando limpieza de imágenes:", cleanError);
      }
      setAudiencia(updatedAudiencia);
      onAudienciaChange && onAudienciaChange(updatedAudiencia);
      fetchDatosIniciales();
      console.log("Audiencia actualizada correctamente");
    } catch (error) {
      console.error("Error al actualizar las deudas o la audiencia:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleAbrirEtapa = async () => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas abrir la Etapa de Resultado?`
    );
    if (!confirmAction) return;
    try {
      setSaving(true);
      const updatedAudiencia = {
        ...audiencia,
        etapas: audiencia.etapas.map((etapa) =>
          etapa.orden === ordenActual
            ? {
                ...etapa,
                estado: "PENDIENTE",
              }
            : etapa
        ),
      };
      await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);
      setAudiencia(updatedAudiencia);
      onAudienciaChange && onAudienciaChange(updatedAudiencia);
      fetchDatosIniciales();
    } catch (error) {
      console.error("Error al abrir la Etapa de Resultado", error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, acreedor) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setAcreedoresDeudas((prev) => ({
      ...prev,
      [acreedor]: {
        ...prev[acreedor],
        [field]: value,
      },
    }));
  };

  const handleEditorPropuestaPagoChange = useCallback(
    (value) => {
      setPropuestaPago(value);
      clearError();
    },
    [clearError]
  );

  const handleEditorResolucionPagoChange = useCallback(
    (value) => {
      setResolucionPago(value);
      clearError();
    },
    [clearError]
  );

  const handleEditorConsideracionesChange = useCallback(
    (value) => {
      setConsideraciones(value);
      clearError();
    },
    [clearError]
  );

  const tieneAcuerdo = resultado && resultado.toUpperCase().includes("ACUERDO");
  const tieneFracaso = resultado && resultado.toUpperCase().includes("FRACASO");

  const isDisabled = () => {
    return (
      !verificacion ||
      initialAudiencia.estado === "SUSPENDIDA" ||
      initialAudiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      verificacion.estadoActual === "CUMPLIDA" ||
      saving ||
      uploadingImage // DESHABILITAR MIENTRAS SE SUBE IMAGEN
    );
  };

  const isOpenEtapaDisabled = () => {
    return (
      !verificacion ||
      initialAudiencia.estado === "SUSPENDIDA" ||
      initialAudiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      verificacion.isEtapaActualPendiente ||
      verificacion.isEtapaActualProgramada ||
      //verificacion.isEtapaSiguienteCumplida ||
      saving ||
      uploadingImage // DESHABILITAR MIENTRAS SE SUBE IMAGEN
    );
  };

  return (
    <Container>
      {(loading || saving || uploadingImage) && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
              {uploadingImage ? "Subiendo imagen..." : saving ? "Guardando..." : "Cargando..."}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {imageError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {imageError}
        </Alert>
      )}

      {proceso && audiencia ? (
        <>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiTypography variant="h4" fontWeight="bold" sx={{ mt: 2, mb: 2 }}>
                Resultado de la Votación:{" "}
                <span style={{ color: resultado?.includes("FRACASO") ? "#e74c3c" : "#4CAF50" }}>
                  {resultado}
                </span>
              </SuiTypography>
              <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                <Table size="small">
                  <TableRow sx={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        color: "white",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#4CAF50" }}>% Positivo</span>
                      <br />
                      <span style={{ color: "#4CAF50" }}>{porcPostivo} %</span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        color: "white",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#e74c3c" }}>% Negativo</span>
                      <br />
                      <span style={{ color: "#e74c3c" }}>{porcNegativo} %</span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        color: "white",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span>% Ausente</span>
                      <br />
                      <span>{porcAusente} %</span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        color: "white",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#666" }}>% Sin Voto</span>
                      <br />
                      <span style={{ color: "#666" }}>{porcSinVoto} %</span>
                    </TableCell>
                  </TableRow>
                </Table>
              </TableContainer>
            </Grid>

            {/* Solo mostrar Instrucciones de Pago si el resultado contiene "ACUERDO" */}
            {tieneAcuerdo && (
              <>
                <Grid size={12}>
                  <SuiTypography variant="h4" fontWeight="bold" sx={{ mt: 2 }}>
                    Instrucciones de Pago a Acreedores
                  </SuiTypography>
                  {Object.entries(acreedoresDeudas).map(([acreedor, data]) => (
                    <Grid container alignItems="center" spacing={2} key={acreedor} sx={{ mt: 1 }}>
                      <Grid item size={6}>
                        <SuiTypography variant="h4" fontWeight="light">
                          * {acreedor}
                        </SuiTypography>
                      </Grid>
                      <Grid item size={6}>
                        <Tooltip title="Instrucciones de Pago" arrow>
                          <SuiInput
                            value={data.instruccion_pago || ""}
                            onChange={handleChange("instruccion_pago", acreedor)}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                height: "200px",
                              },
                            }}
                            disabled={isDisabled()}
                          />
                        </Tooltip>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid size={12} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                  <SuiTypography variant="h4" fontWeight="bold">
                    Consideraciones{" "}
                    <Box component="span" sx={{ fontSize: "0.65em" }}>
                      (a partir del Punto 20 del Acta)
                    </Box>
                  </SuiTypography>
                </Grid>
                <Grid size={12}>
                  <StyledEditor
                    ref={quillConsideracionesRef}
                    value={consideraciones}
                    onChange={handleEditorConsideracionesChange}
                    modules={getQuillModules()}
                    formats={getQuillFormats()}
                    readOnly={isDisabled()}
                    placeholder="Ingrese las consideraciones del resultado. Puede insertar imágenes usando el botón de imagen en la barra de herramientas."
                  />
                </Grid>
              </>
            )}

            {/* Solo mostrar Instrucciones de Pago si el resultado contiene "FRACASO" */}
            {tieneFracaso && (
              <>
                <Grid size={12} sx={{ mt: 2 }}>
                  <SuiTypography variant="h4" fontWeight="bold">
                    Acuerdo Bilateral
                  </SuiTypography>
                </Grid>
                <Grid size={12}>
                  {Object.entries(acreedoresDeudas).map(([acreedor, data]) => (
                    <Grid container alignItems="center" spacing={2} key={acreedor} sx={{ mt: 1 }}>
                      <Grid item size={6}>
                        <SuiTypography variant="h4" fontWeight="light">
                          - {acreedor}
                        </SuiTypography>
                      </Grid>
                      <Grid item size={6}>
                        <InputLabel sx={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            checked={data.acuerdo_bilateral}
                            disabled={isDisabled()}
                            onChange={handleChange("acuerdo_bilateral", acreedor)}
                            size="small"
                            sx={{
                              color: "white",
                              "&.Mui-checked": {
                                color: "white",
                              },
                              padding: "4px",
                            }}
                          />
                          <SuiTypography variant="h6" fontWeight="light">
                            ¿Acuerdo Bilateral?
                          </SuiTypography>
                        </InputLabel>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {tieneFracaso && Object.values(acreedoresDeudas).some((a) => a.acuerdo_bilateral) && (
              <>
                <Grid size={6}>
                  <SuiTypography variant="h5" fontWeight="bold">
                    Propuesta de Pago
                  </SuiTypography>
                  <StyledEditor
                    ref={quillPropuestaPagoRef}
                    value={propuestaPago}
                    onChange={handleEditorPropuestaPagoChange}
                    modules={getQuillModules()}
                    formats={getQuillFormats()}
                    readOnly={isDisabled()}
                    placeholder="Ingrese la Propuesta de Pago del acuerdo bilateral."
                  />
                </Grid>
                <Grid size={6}>
                  <SuiTypography variant="h5" fontWeight="bold">
                    Resolución de Pago
                  </SuiTypography>
                  <StyledEditor
                    ref={quillResolucionPagoRef}
                    value={resolucionPago}
                    onChange={handleEditorResolucionPagoChange}
                    modules={getQuillModules()}
                    formats={getQuillFormats()}
                    readOnly={isDisabled()}
                    placeholder="Ingrese la Resolución del acuerdo bilateral."
                  />
                </Grid>
              </>
            )}
          </Grid>
          {(roles.isAdmin || roles.isOperador) && (
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={8} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <InputLabel sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={checkedCumplida}
                    disabled={isDisabled()}
                    onChange={(e) => setCheckedCumplida(e.target.checked)}
                    size="small"
                    sx={{
                      color: "white",
                      "&.Mui-checked": {
                        color: "white",
                      },
                      padding: "4px",
                    }}
                  />
                  <SuiTypography variant="h6" fontWeight="light">
                    ¿Cumplida?
                  </SuiTypography>
                </InputLabel>
                <SuiButton
                  variant="gradient"
                  buttonColor="info"
                  onClick={handleSave}
                  disabled={isDisabled()}
                >
                  Guardar Etapa
                </SuiButton>
                <SuiTypography variant="caption" fontWeight="bold">
                  {!verificacion
                    ? "Cargando verificación..."
                    : audiencia.estado === "SUSPENDIDA"
                    ? "- Audiencia suspendida (no modificable)"
                    : !verificacion.isQuorumCumplido && ordenActual > 1
                    ? "- No se puede avanzar: Quórum no completado"
                    : !verificacion.isEtapaAnteriorCumplida && ordenActual > 1
                    ? "- Complete la etapa anterior primero"
                    : !verificacion.isEtapaActualPendiente && !verificacion.isEtapaActualProgramada
                    ? "- Esta etapa ya fue completada"
                    : ordenActual === 1 && verificacion.estadoActual !== "CUMPLIDA"
                    ? "- Quórum pendiente de completar"
                    : uploadingImage
                    ? "- Subiendo imagen..."
                    : null}
                </SuiTypography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={handleAbrirEtapa}
                  disabled={isOpenEtapaDisabled()}
                >
                  Abrir Etapa
                </SuiButton>
              </Grid>
            </Grid>
          )}
        </>
      ) : (
        <Typography>Cargando...</Typography>
      )}
    </Container>
  );
};

ContainerResultado.propTypes = {
  proceso: PropTypes.object,
  audiencia: PropTypes.object,
  onAudienciaChange: PropTypes.func.isRequired,
};

export default ContainerResultado;
